import { CurrencyPipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { RecipientOutstandingAggregates } from '@anduin/api';
import { ConfigService } from '@anduin/config';
import { isEmpty } from '@anduin/shared';

const KEY_TO_ORDINAL_MAP: Record<IterableClientCadenceAggregateKey, number> = {
  currentDue: 0,
  underOneMonthDue: 1,
  oneToTwoMonthsDue: 2,
  twoToThreeMonthsDue: 3,
  threeToFourMonthsDue: 4,
  // overThreeMonthsDue: 5,
  overFourMonthsDue: 6,
  totalDue: 7,
};

type IterableClientCadenceAggregateKey = keyof Omit<
  RecipientOutstandingAggregates,
  'overThreeMonthsDue' | 'recipientId' | 'currencyCode' | 'currencySymbol'
>;

const KEY_TO_AMOUNT_COLOR_MAP: Record<IterableClientCadenceAggregateKey, `color-${string}-${number}`> = {
  currentDue: 'color-green-300',
  underOneMonthDue: 'color-gold-300',
  oneToTwoMonthsDue: 'color-gold-300',
  twoToThreeMonthsDue: 'color-gold-300',
  threeToFourMonthsDue: 'color-red-300',
  // overThreeMonthsDue: 'color-red-300',
  overFourMonthsDue: 'color-red-300',
  totalDue: 'color-gray-100',
};

const KEY_TO_TITLE_MAP: Record<IterableClientCadenceAggregateKey, string> = {
  currentDue: 'Current',
  underOneMonthDue: '1-30 days',
  oneToTwoMonthsDue: '31-60 days',
  twoToThreeMonthsDue: '61-90 days',
  threeToFourMonthsDue: '91-120 days',
  // overThreeMonthsDue: 'Over 90 days',
  overFourMonthsDue: 'Over 120 days',
  totalDue: 'Total Balance',
};

interface OutstandingBalanceDisplayItem {
  title: string;
  amountColor: `color-${string}-${number}`;
  amount: string;
  isTimeRange: boolean;
}

@Component({
  selector: 'outstanding-balance-by-age',
  templateUrl: './outstanding-balance-by-age.component.html',
  styleUrls: ['./outstanding-balance-by-age.component.scss'],
  standalone: false,
  host: {
    class: 'rounded position-relative',
  },
})
export class OutstandingBalanceByAgeComponent {
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly configService = inject(ConfigService);

  outstandingByAge = input<RecipientOutstandingAggregates>();
  outstandingByAgeDisplaySections = computed<OutstandingBalanceDisplayItem[]>(() => {
    if (isEmpty(this.outstandingByAge())) {
      return undefined;
    }

    const sortedEntries = (Object.entries(this.outstandingByAge()) as [IterableClientCadenceAggregateKey, number][])
      .filter(([key]) => Number.isInteger(KEY_TO_ORDINAL_MAP[key])) // remove irrelevant/undesired fields from UI
      .sort(([keyA], [keyB]) => KEY_TO_ORDINAL_MAP[keyA] - KEY_TO_ORDINAL_MAP[keyB]);

    return sortedEntries.map(([key, value]: [IterableClientCadenceAggregateKey, number]) => ({
      title: KEY_TO_TITLE_MAP[key],
      amountColor: KEY_TO_AMOUNT_COLOR_MAP[key],
      // multicurrency support to be added
      amount: value || key === 'totalDue' ? this.currencyPipe.transform(value, this.outstandingByAge().currencyCode) : undefined,
      isTimeRange: !['currentDue', 'totalDue'].includes(key),
    }));
  });

  shouldShowLateFeeMessage = computed(() => {
    return !this.configService.smartStatementIncludeLateFeeInTotal;
  });

  lateFeeDisplayName = computed(() => {
    const displayName = this.configService.lateFeeDisplayName || 'Finance Charges';

    const formattedName = displayName
      .split(' ')
      .map((each) => each.toLocaleUpperCase()[0] + each.slice(1))
      .join(' ');

    if (formattedName[formattedName.length - 1] !== 's') {
      return formattedName + 's';
    }

    return formattedName;
  });
}
